import React, { useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import ClickOutside from './ClickOutside';
import Text from './Text';
import Icon from './Icon';
import Popup from './Popup';
import Menu from './Menu';
import Separator from './Separator';
import MenuItem from './MenuItem';
import EmptyButton from './EmptyButton';

const StyledButton = styled(EmptyButton)`
  cursor: pointer;
  text-decoration: none;
  &.open {
    border-color: ${props => props.theme.border.border700};
  }
`;

const StyledPopup = styled(Popup)`
  left: ${props => (props.position === 'right' ? 'auto' : '0px')};
  right: ${props => (props.position === 'right' ? '0px' : 'auto')};
  min-width: ${props => props.minPopupWidth || '100%'};
`;

const defaultRender = data => {
  const { splitter, ...props } = data;

  if (splitter) {
    return <Separator paddingVertical="small" color="background400" />;
  }
  return <MenuItem {...props} />;
};

const defaultButtonRender = ({ show, setShow, selectedText, ...props }) => (
  <StyledButton
    className={show ? 'open' : ''}
    onClick={() => setShow(!show)}
    height="42px"
    width="100%"
    display="flex"
    justifyContent="space-between"
    alignItems="center"
    padding="0"
    backgroundColor="background100"
    borderRadius="21px"
    border="1px solid"
    borderColor="border600"
    paddingTop="small"
    paddingRight="normal"
    paddingBottom="small"
    paddingLeft="normal"
    fontSize="16px"
    color="text500"
    {...props}
  >
    <Text whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
      {selectedText}
    </Text>
    <Icon
      marginLeft
      className="ls-icon icon-generalcaretdownmedium"
      iconSize="medium"
    />
  </StyledButton>
);

const DropDown = ({
  onSelected,
  data,
  render,
  buttonRender,
  position,
  minPopupWidth,
  ...props
}) => {
  const [show, setShow] = useState(false);

  const onRender = useCallback(
    (item, index) => {
      const onClick = event => {
        onSelected(item, index, event);
        setShow(false);
      };
      return render(
        {
          ...item,
          icon: item.selected ? 'ls-icon icon-generalticklarge' : undefined,
          onClick,
        },
        index
      );
    },
    [onSelected, render]
  );

  const selectedText = useMemo(() => {
    const item = data.find(({ selected }) => selected);
    const { selectedText: primary, text } = item || {};
    return primary || text || '';
  }, [data]);

  return (
    <ClickOutside onClickOutside={() => setShow(false)} {...props}>
      {buttonRender({ show, setShow, selectedText, ...props })}
      <StyledPopup
        show={show}
        position={position}
        minPopupWidth={minPopupWidth}
      >
        <Menu data={data} render={onRender} />
      </StyledPopup>
    </ClickOutside>
  );
};

DropDown.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({})),
  onSelected: PropTypes.func,
  render: PropTypes.func,
  buttonRender: PropTypes.func,
  position: PropTypes.string,
  minPopupWidth: PropTypes.string,
};

DropDown.defaultProps = {
  data: [],
  onSelected: () => {},
  render: defaultRender,
  buttonRender: defaultButtonRender,
  position: null,
  minPopupWidth: null,
};

export default DropDown;
